import { Intercom, shutdown, update } from '@intercom/messenger-js-sdk';
import { useEffect, useRef, useState } from 'react';
import { useApi } from '@/hooks/useApi';
import { intercomService } from '@/services/intercomService';
import type { IntercomUser } from '@/types/intercom';

const FALLBACK_INTERCOM_APP_ID = 'k5nnj6yq';

interface UseIntercomOptions {
  user: IntercomUser | null;
  enabled?: boolean;
  intercomAppId?: string;
}

interface UseIntercomResult {
  isLoading: boolean;
  isInitialized: boolean;
  error: Error | null;
}

export function useIntercom(options: UseIntercomOptions): UseIntercomResult {
  const { user, enabled = true, intercomAppId } = options;
  const { apiGet } = useApi();
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const initRef = useRef(false);
  const apiGetRef = useRef(apiGet);
  const nameRef = useRef(user?.name);

  const appId = intercomAppId || FALLBACK_INTERCOM_APP_ID;
  const email = user?.email;
  const instanceId = user?.instanceId;

  useEffect(() => {
    apiGetRef.current = apiGet;
    nameRef.current = user?.name;
  });

  // Initialize Intercom once. name is accessed via ref to avoid
  // dep-change cleanups that queue a shutdown before the widget loads.
  useEffect(() => {
    if (!enabled || !email || !instanceId || initRef.current) {
      setIsLoading(false);
      return;
    }

    const initIntercom = async (): Promise<void> => {
      try {
        setIsLoading(true);

        const userId = `${instanceId}-${email}`.toLowerCase();

        let userHash: string | undefined;
        try {
          userHash = await intercomService.fetchUserSignature(apiGetRef.current);
        } catch {
          // Identity verification is optional — proceed without it if the backend is unavailable
        }

        if (initRef.current) return;

        console.log('[Intercom] Initializing with:', {
          app_id: appId,
          user_id: userId,
          email,
          name: nameRef.current,
          hasHash: !!userHash,
        });

        Intercom({
          app_id: appId,
          user_id: userId,
          email,
          name: nameRef.current,
          user_hash: userHash,
          messenger_style_id: 2523663,
        });

        console.log('[Intercom] SDK called. window.intercomSettings:', window.intercomSettings);

        initRef.current = true;
        setIsInitialized(true);
      } catch (err) {
        console.error('[Intercom] Initialization failed:', err);
        if (!initRef.current) {
          setError(err instanceof Error ? err : new Error('Failed to initialize Intercom'));
        }
      } finally {
        setIsLoading(false);
      }
    };

    initIntercom();

    const handleBeforeUnload = (): void => {
      shutdown();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (initRef.current) {
        shutdown();
        initRef.current = false;
      }
    };
  }, [email, instanceId, enabled, appId]);

  useEffect(() => {
    if (initRef.current && user?.name) {
      update({ name: user.name });
    }
  }, [user?.name]);

  return { isLoading, isInitialized, error };
}
